import { Box, Button } from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-calculate'
import get from 'lodash/get'
import merge from 'lodash/merge'
import { useEffect } from 'react'
import {
  BooleanInput,
  CheckboxGroupInput,
  FormDataConsumer,
  Labeled,
  NumberInput,
  Record,
  required,
  sanitizeEmptyValues,
  TextInput,
  useDataProvider,
  useInput,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Field, Form } from 'react-final-form'
import { LocationDropdown } from 'shared/components/Dropdown/LocationDropdown'
import { PROVIDER_CHOICES } from 'shared/helpers/constants'
import { loadGoogleMapApis } from 'shared/helpers/loadGoogleMapApis'

import { getLoggedInUserIdentityWithCache } from 'api/identity'
import { captureExceptionIfProduction } from 'helpers/utils'
import { generateSearch } from '../../api/searches'
import { SUPPORTED_EQUIPMENT_CHOICES } from '../../helpers/constants'

const validateRequired = [required()]

const PickupDateInput = (props: any) => {
  const {
    input: { onChange, value },
  } = useInput(props)

  let dateValue = value !== '' ? new Date(value) : null

  return (
    <DatePicker
      value={dateValue}
      autoOk
      onChange={onChange}
      format="MM/dd/yyyy"
      required
      error={!dateValue}
      helperText={dateValue ? '' : 'Required'}
    />
  )
}

/**
 * Format form values from SearchCreate for request to backend API
 * @param {object} values
 * @return {object}
 */
export function SearchFormToAPI(record: Record, values: any) {
  values = sanitizeEmptyValues(record, values)

  const date = new Date(values['date_start_min'])
  const date_no_offset = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  const origin_pickup_date__min = date_no_offset.toISOString().slice(0, -1)

  const organized_values = {
    origin_location: values['start_location'],
    dest_location: values['end_location'] ? values['end_location'] : '',
    origin_pickup_date__min,
    only_specified_date: true,
    origin_range_mi__max: values['deadhead_mi_max'],
    dest_range_mi__max: values['deadhead_mi_max'],
    equipment: values['equipment'],
    // options: BEST_PRICE, NEXT_AVAILABLE, REVENUE_PER_HOUR, TAKE_HOME
    sort_type: 'REVENUE_PER_HOUR',
    truck_weight_lb__max: values['truck_weight_lb'],
    booking_type: values['instant_book_only'] ? 'INSTANT' : 'ALL',
    include_multi_stop: true,
    exclude_single_stop: false,
    previous_query_id: values['previous_query_id'],
    load_id: values['load_id'],
    preload: get(values, 'preload', false),
  }

  return organized_values
}

/**
 * Format serialized search record to form values for SearchCreate
 * Used to pre-populate form to regenerate (+edit) a search.
 * @param {object} record
 * @return {object}
 */
export function SearchRecordtoForm(record: any) {
  const filter_options = get(record, 'filter_options', null)
  const load_id = get(record, 'console_metadata.load_id', null)
  const preload = get(record, 'console_metadata.preload', false)

  if (!filter_options) {
    return {}
  }

  const custom_values = {
    start_location: filter_options['origin_location'],
    end_location: filter_options['dest_location'],
    deadhead_mi_max: filter_options['origin_range_mi__max'],
    equipment: filter_options['equipment'],
    truck_weight_lb: filter_options['truck_weight_lb__max'],
    instant_book_only: filter_options['booking_type'] === 'INSTANT',
    previous_query_id: record.id,
    load_id: load_id,
    preload: preload,
  }

  return custom_values
}

const GenerateSearchForm = ({ record, version, initialValues }: any) => {
  const redirectTo = useRedirect()
  const notify = useNotify()
  const dataProvider = useDataProvider()

  useEffect(() => {
    async function fetchUser() {
      const { web_google_api_key } = await getLoggedInUserIdentityWithCache()
      loadGoogleMapApis(web_google_api_key, () => null)
    }

    fetchUser()
  }, [])

  // Populate the start location when a previous load is specified
  const calculator = createDecorator({
    field: /(load_id|preload)/,
    updates: (value, name, allValues: any) => {
      const load_id = allValues['load_id']
      const preload = allValues['preload']

      if (load_id) {
        return dataProvider
          .getOne('loads', { id: load_id })
          .then(({ data }) => {
            const start_location = preload ? null : `${data['dest_city']}, ${data['dest_state']}`
            const end_location = preload ? `${data['origin_city']}, ${data['origin_state']}` : null
            const previous_load_delivery = data['dest_delivery_date']
            const next_load_pickup = data['origin_pickup_date']
            const date_start_min = preload ? null : previous_load_delivery
            const date_end_max = preload ? next_load_pickup : null

            return {
              start_location,
              end_location,
              date_start_min,
              date_end_max,
              previous_load_delivery,
              next_load_pickup,
            }
          })
          .catch((error) => {
            notify(`ERROR: Couldn't fetch details for load ${load_id}`, { type: 'warning' })
            captureExceptionIfProduction(error)
          })
      }

      return {}
    },
  })

  const submit = (values: any) => {
    const organized_values = SearchFormToAPI(record, values)
    generateSearch(organized_values, notify, redirectTo)
  }

  var initialValuesObject = merge(
    {
      date_start_min: new Date(),
      equipment: ['DRY_VAN'],
      truck_weight_lb: 45000,
      deadhead_mi_max: 100,
      load_providers: PROVIDER_CHOICES.map((item) => item.id),
    },
    initialValues
  )

  return (
    <Form
      initialValues={initialValuesObject}
      onSubmit={submit}
      mutators={{ ...arrayMutators }}
      decorators={[calculator]}
      key={version}
      render={(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <Box display="flex">
              <Box flex={1}>
                <CheckboxGroupInput
                  source="equipment"
                  label="Equipment Types"
                  choices={SUPPORTED_EQUIPMENT_CHOICES}
                  validate={validateRequired}
                />
              </Box>
              <Box flex={1}>
                <NumberInput source="truck_weight_lb" label="Max Load Weight (lbs)" min="0" step="1000" />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <Labeled label="Pickup Date">
                  <PickupDateInput name="date_start_min" />
                </Labeled>
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <Field
                      name="start_location"
                      validate={required('The start location is required')}
                      render={({ input, meta }) => {
                        return (
                          <LocationDropdown
                            {...input}
                            error={meta.error && meta.touched}
                            name="start_location"
                            width={300}
                            inputProps={{
                              name: 'start_location',
                              label: 'Start Location (required)',
                              disabled: formData.load_id && !formData.preload,
                              maxWidth: '300px',
                            }}
                          />
                        )
                      }}
                    />
                  )}
                </FormDataConsumer>
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <Field
                      name="end_location"
                      render={({ input }) => {
                        return (
                          <LocationDropdown
                            {...input}
                            name="end_location"
                            width={300}
                            inputProps={{
                              name: 'end_location',
                              label: 'End Location (optional)',
                              disabled: formData.load_id && formData.preload,
                              maxWidth: '300px',
                            }}
                          />
                        )
                      }}
                    />
                  )}
                </FormDataConsumer>
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <NumberInput
                  source="deadhead_mi_max"
                  label="Max. Deadhead Miles"
                  min="0"
                  max="1000"
                  step="1"
                  helperText="(Applied separately to pickup and dropoff)"
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <BooleanInput source="instant_book_only" label="Instant Book Only" />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <TextInput
                  source="previous_query_id"
                  label="Previous Search"
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) => (
                    <TextInput
                      source="load_id"
                      label="Load ID"
                      helperText={
                        formData.load_id &&
                        `
                          ${formData.preload ? 'Next' : 'Previous'} load
                          ${formData.preload ? 'picks up' : 'delivers'} at
                          ${formData.preload ? formData.end_location : formData.start_location} on
                          ${(
                            (formData.preload
                              ? formData.next_load_pickup
                              : formData.previous_load_delivery) || ''
                          ).slice(0, 10)}
                        `
                      }
                      InputLabelProps={{ shrink: true }}
                      disabled
                    />
                  )}
                </FormDataConsumer>
              </Box>
            </Box>
            <Box display="flex">
              <Box flex={1}>
                <FormDataConsumer>
                  {({ formData }) =>
                    formData.load_id && (
                      <BooleanInput source="preload" label={`${formData.preload ? 'Preload' : 'Reload'}`} />
                    )
                  }
                </FormDataConsumer>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" width="100%">
              <Button variant="contained" color="primary" type="submit">
                Submit
              </Button>
            </Box>
          </form>
        )
      }}
    />
  )
}

export default function SearchCreate(props: any) {
  const initialValues = get(props, `location.state.initial_values`, {})
  return <GenerateSearchForm initialValues={initialValues} />
}
